import * as AvatarPrimitive from 'radix-ui/avatar';
import { cx } from '../cx';

/**
 * Initials by default; an image only when there is one. No coloured
 * auto-generated backgrounds - a wall of pastel circles is exactly the kind of
 * decoration this design direction rejects.
 *
 * IT IS A FILLED DISC (WP6b). It used to be a hairline ring over the sunken
 * surface with muted text inside, and at 24px that reads as an empty pill with
 * something faint in it rather than as a stand-in for a person. A monogram has
 * to have the visual weight of the photo it replaces, or the row looks broken
 * wherever a photo is missing. Substance comes from the fill and the weight,
 * not from colour: see --color-monogram in tokens.css for why the light/dark
 * pair is inverted rather than tinted.
 *
 * THE MONOGRAM IS SIZED IN PROPORTION, ~44% of the diameter, which is what
 * keeps two capitals optically centred in a circle at three different sizes.
 * `sm` is 28px rather than 24 for exactly this reason: 13px is this product's
 * floor for a label, and 13px inside 24px is 54% - cramped, and the single
 * loudest thing about the old avatar.
 *
 * `tracking-normal` cancels the body tracking. <body> asks for -0.026em, which
 * is the right optical value for 17px prose and the wrong one for two capitals
 * that should sit apart, not touch.
 */
export type AvatarSize = 'sm' | 'md' | 'lg';

export interface AvatarProps {
  readonly name: string;
  readonly src?: string | undefined;
  readonly size?: AvatarSize | undefined;
  readonly className?: string | undefined;
}

const SIZES: Record<AvatarSize, string> = {
  sm: 'size-7 text-2xs', // 28px / 13px = 46%
  md: 'size-8 text-xs', // 32px / 14px = 44%
  lg: 'size-12 text-md', // 48px / 19px = 40%
};

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  return `${first.slice(0, 1)}${last.slice(0, 1)}`.toUpperCase();
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  return (
    <AvatarPrimitive.Root
      className={cx(
        'inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill',
        'bg-monogram font-semibold tracking-normal text-monogram-ink select-none',
        SIZES[size],
        className,
      )}
    >
      {src !== undefined && (
        <AvatarPrimitive.Image src={src} alt={name} className="size-full object-cover" />
      )}
      {/* `leading-none`: a line box taller than the capitals pushes the monogram
          off the circle's centre by half the leading. */}
      <AvatarPrimitive.Fallback delayMs={src === undefined ? 0 : 300} className="leading-none">
        {initials(name)}
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}
